import * as React from 'react';
import { Loader2, X } from 'lucide-react';
import type { CanvasProjectionPaneProps } from '../../../components/canvas/projections/CanvasProjectionRegistry';
import { getCommandManager } from '../../../hooks/useCommandManager';
import { useDiagramAccess } from '../../../hooks/useDiagramAccess';
import { useDiagramInfo } from '../../../hooks/useDiagramInfo';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { useMultiCanvasViewStore } from '../../../stores/multiCanvasViewStore';
import { toast } from 'sonner';
import { useOrgModel } from '../hooks/useOrgModel';
import { useOptimisticPositionStructureModel } from '../hooks/useOptimisticPositionStructureModel';
import { usePositionChartGrouping } from '../hooks/usePositionChartGrouping';
import { useAtlasAccessSummary } from '../hooks/useAtlasAccessSummary';
import { isFullOrgLiveView, isOperationalOrgView } from '../models/orgArtifactKind';
import { SUCCESSION_PLANNING_VIEW_ID } from '../assistant/OrgChartRecipes';
import { ensureOrgProjectionDiagrams } from '../hooks/useEnsureOrgProjectionDiagrams';
import {
  resolveSuccessionAuthoringShapeId,
  successionAuthoringUrl,
} from '../services/successionAuthoringNavigation';
import { mapAtlasActionState } from '../services/atlasActionStatePresentation';
import { PositionStaffingDrawer } from './PositionStaffingDrawer';

export const POSITION_STAFFING_PROJECTION_KIND = 'atlas-position-staffing';

export const PositionStaffingWorkbenchPane: React.FC<CanvasProjectionPaneProps> = ({
  diagramId,
}) => {
  const diagram = useDiagramInfo(diagramId);
  const access = useDiagramAccess(diagramId);
  const authenticatedFetch = useAuthenticatedFetch();
  const closeProjection = useMultiCanvasViewStore((state) => state.closeProjection);
  const folderId = diagram.data?.folderId;
  const planningAccess = useAtlasAccessSummary({ folderId, diagramId });
  const orgModel = useOrgModel({
    folderId,
    enabled: Boolean(folderId),
  });
  const model = useOptimisticPositionStructureModel(folderId, orgModel.data);
  const grouping = usePositionChartGrouping(
    folderId,
    diagram.data?.id,
    diagram.data?.viewDefinition,
  );
  const isMasterChart = isFullOrgLiveView(diagram.data?.viewDefinition);
  const isOperationalChart = isOperationalOrgView(diagram.data?.viewDefinition);
  const editPositionsAccess = mapAtlasActionState(
    planningAccess.actionState('editPositions'),
  );
  const executeOperationalAccess = mapAtlasActionState(
    planningAccess.actionState('executeOperationalChange'),
  );
  const canWrite = Boolean(access.data?.canWrite && editPositionsAccess.enabled);
  const controlsAccessMessage = !access.data?.canWrite
    ? 'You need diagram edit access to change staffing.'
    : !editPositionsAccess.enabled
      ? editPositionsAccess.message
      : isOperationalChart && !executeOperationalAccess.enabled
        ? executeOperationalAccess.cta === 'request_access'
          ? `${executeOperationalAccess.message} Ask a folder administrator for HR Operator access.`
          : executeOperationalAccess.message
        : null;
  const canManageSuccession = planningAccess.actionState('editSuccession').state === 'enabled';
  const openSuccession = React.useCallback(async (positionId: string) => {
    if (!folderId || isMasterChart || !canManageSuccession) return;
    const shapeId = resolveSuccessionAuthoringShapeId({ positionId, model });
    if (!shapeId) {
      toast.error('This position has no uniquely resolvable Full Org Chart entity.');
      return;
    }
    try {
      const projections = await ensureOrgProjectionDiagrams(authenticatedFetch, folderId);
      closeProjection();
      window.location.href = successionAuthoringUrl({
        folderId,
        diagramId: projections.primaryDiagramId,
        shapeId,
        reviewId: SUCCESSION_PLANNING_VIEW_ID,
      });
    } catch {
      toast.error('The Full Org Chart could not be opened. Try again.');
    }
  }, [authenticatedFetch, canManageSuccession, closeProjection, folderId, isMasterChart, model]);

  if (!folderId || !diagramId || !model) {
    return (
      <aside
        className="relative flex h-full min-h-0 w-full flex-col bg-white"
        aria-label="Position staffing workbench status"
        aria-live="polite"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Staffing workbench</p>
            <p className="mt-0.5 text-xs text-slate-500">People and positions</p>
          </div>
          <button
            type="button"
            onClick={closeProjection}
            className="flex size-10 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close staffing workbench"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center p-6 text-center">
          {diagram.error || orgModel.error ? (
            <div>
              <p className="text-sm font-semibold text-red-800">Position chart unavailable</p>
              <p className="mt-1 text-xs text-slate-600">
                The permissioned organization model could not be loaded.
              </p>
              <button
                type="button"
                className="mt-3 min-h-10 rounded bg-blue-600 px-3 text-xs font-medium text-white"
                onClick={() => void (diagram.error ? diagram.refetch() : orgModel.refetch())}
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="size-4 animate-spin" />
              Loading staffing workbench…
            </div>
          )}
        </div>
      </aside>
    );
  }

  return (
    <PositionStaffingDrawer
      presentation="pane"
      folderId={folderId}
      diagramId={diagramId}
      model={model}
      groupingPolicy={grouping.policy}
      groupingSaving={grouping.isSaving}
      groupingError={grouping.saveError}
      onSaveGrouping={grouping.save}
      canWrite={canWrite}
      canUseScenarios={planningAccess.actionState('applyScenario').state === 'enabled'}
      operational={isOperationalChart}
      canExecuteOperationalChange={executeOperationalAccess.enabled}
      controlsAccessMessage={controlsAccessMessage}
      commandManager={getCommandManager()}
      onClose={closeProjection}
      onManageSuccession={!isMasterChart && canManageSuccession ? openSuccession : undefined}
    />
  );
};
